import * as v from 'valibot'

// This flag helps the bundler tree shake code that is only used in our managed
// environment when building for Enterprise Platform.
const IS_MANAGED_BUILD =
  import.meta.IS_MANAGED_BUILD ??
  import.meta.env.VITE_ENTERPRISE_PLATFORM !== 'true'

const IS_ENTERPRISE_BUILD = !IS_MANAGED_BUILD

const GIT_COMMIT_SHA = (
  import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ||
  import.meta.env.VITE_GIT_COMMIT_SHA
)?.slice(0, 7)

const DEFAULT_CLIENT_VERSION = import.meta.env.DEV ? 'local' : GIT_COMMIT_SHA

// Strategy for exposing config from the server to the client:
// 1. On the server, the config is loaded from (mostly) environment variables on
//    bootstrap.
// 2. The server validates the config against the schema, and errors if it's invalid.
// 3. The server stringifies the config and attaches it to the HTML document.
// 4. On the client, the config is loaded from the HTML document

const EnvironmentSchema = v.union([
  v.literal('production'),
  v.literal('preview'),
  v.literal('development'),
  v.literal('self_hosted'),
])

type Environment = v.InferOutput<typeof EnvironmentSchema>

// This part of the config that must always be available, or the app won't work.
const BaseConfigSchema = v.object({
  ENVIRONMENT: EnvironmentSchema,

  GRAFBASE_API_HOST: v.pipe(v.string(), v.url()),
  WEBSITE_URL: v.optional(v.pipe(v.string(), v.url()), 'https://grafbase.com'),

  CLIENT_NAME: v.optional(v.string(), 'Grafbase Dashboard'),
  CLIENT_VERSION: DEFAULT_CLIENT_VERSION
    ? v.optional(v.string(), DEFAULT_CLIENT_VERSION)
    : v.string(),
})

// This part of the config is only provided for our managed environment.
const ManagedConfigSchema = v.object({
  ENABLE_TESTING_AUTH: v.optional(v.boolean(), false),

  SENTRY: v.optional(
    v.object({
      DSN: v.optional(v.string()),
      ENABLE_TUNNEL: v.optional(v.boolean(), false),
    }),
  ),

  ENABLE_LINKEDIN_ANALYTICS: v.optional(v.boolean(), false),
  ENABLE_VERCEL_ANALYTICS: v.optional(v.boolean(), false),
  ENABLE_VERCEL_SPEED_INSIGHTS: v.optional(v.boolean(), false),

  GOOGLE_TAG_MANAGER_CONTAINER_ID: v.optional(v.string()),
  SYFT_SOURCE_ID: v.optional(v.string()),

  RUDDERSTACK_WRITE_KEY: v.optional(v.string()),
  RUDDERSTACK_DATAPLANE_URL: v.optional(v.pipe(v.string(), v.url())),

  HYPERTUNE_TOKEN: v.optional(v.string()),
  EDGE_CONFIG: v.optional(v.string()),
  EDGE_CONFIG_HYPERTUNE_ITEM_KEY: v.optional(v.string()),
})

const EnterpriseConfigSchema = v.object({
  /** The product this instance is running: 'graphql' or 'nexus' */
  EP_PRODUCT: v.optional(
    v.union([v.literal('graphql'), v.literal('nexus')]),
    'graphql',
  ),
  /**  The in-cluster address, for server to server communication */
  ZITADEL_HOST_INTERNAL: v.optional(v.pipe(v.string(), v.url())),

  /** The publicly available address, for browser access */
  ZITADEL_HOST_PUBLIC: v.optional(v.pipe(v.string(), v.url())),

  /** The internal Grafbase API host, used for server to server communication in EP platform */
  GRAFBASE_API_HOST_INTERNAL: v.pipe(v.string(), v.url()),

  /** OIDC provider type: 'zitadel' or 'generic' */
  OIDC_PROVIDER_TYPE: v.optional(
    v.union([v.literal('zitadel'), v.literal('generic')]),
    'zitadel',
  ),

  /** Generic OIDC issuer URL (used for both internal and public by default) */
  OIDC_ISSUER: v.optional(v.pipe(v.string(), v.url())),

  /** OIDC scopes to request at the beginning of the Authorization Code Grant flow */
  OIDC_SCOPES: v.optional(v.string()),

  /** Whether to show sign-up hint on authorization */
  OIDC_SIGNUP_HINT: v.optional(v.boolean(), false),

  /** Optional access_type parameter for OIDC authorization (e.g., 'offline' for Google OAuth) */
  OIDC_ACCESS_TYPE: v.optional(v.string()),
})

function getConfigFromEnvVars() {
  let ENVIRONMENT: Environment

  if (import.meta.env.DEV) {
    ENVIRONMENT = 'development'
  } else if (IS_ENTERPRISE_BUILD) {
    ENVIRONMENT = 'self_hosted'
  } else if (process.env.VERCEL_ENV)
    ENVIRONMENT = process.env.VERCEL_ENV as Environment
  else {
    throw new Error('Invalid environment value')
  }

  const baseConfig = {
    ENVIRONMENT,
    WEBSITE_URL: process.env.WEBSITE_URL,
    GRAFBASE_API_HOST: process.env.GRAFBASE_API_HOST,

    CLIENT_NAME: process.env.CLIENT_NAME,
    CLIENT_VERSION: process.env.CLIENT_VERSION,
  }

  if (IS_ENTERPRISE_BUILD)
    return {
      ...baseConfig,
      EP_PRODUCT: process.env.EP_PRODUCT,
      ZITADEL_HOST_INTERNAL: process.env.ZITADEL_HOST_INTERNAL,
      ZITADEL_HOST_PUBLIC: process.env.ZITADEL_HOST_PUBLIC,
      GRAFBASE_API_HOST_INTERNAL: process.env.GRAFBASE_API_HOST_INTERNAL,
      OIDC_PROVIDER_TYPE: process.env.OIDC_PROVIDER_TYPE,
      OIDC_ISSUER: process.env.OIDC_ISSUER,
      OIDC_ISSUER_INTERNAL: process.env.OIDC_ISSUER_INTERNAL,
      OIDC_SCOPES: process.env.OIDC_SCOPES,
      OIDC_SIGNUP_HINT: process.env.OIDC_SIGNUP_HINT === 'true',
      OIDC_ACCESS_TYPE: process.env.OIDC_ACCESS_TYPE,
    }

  const managedConfig = {
    ENABLE_TESTING_AUTH: process.env.ENABLE_TESTING_AUTH === 'true',

    ENABLE_LINKEDIN_ANALYTICS: process.env.ENABLE_LINKEDIN_ANALYTICS === 'true',
    ENABLE_VERCEL_ANALYTICS:
      process.env.ENABLE_VERCEL_ANALYTICS === 'true' &&
      Boolean(process.env.VERCEL_URL),
    ENABLE_VERCEL_SPEED_INSIGHTS:
      process.env.ENABLE_VERCEL_SPEED_INSIGHTS === 'true' &&
      Boolean(process.env.VERCEL_URL),

    GOOGLE_TAG_MANAGER_CONTAINER_ID:
      process.env.GOOGLE_TAG_MANAGER_CONTAINER_ID,
    SYFT_SOURCE_ID: process.env.SYFT_SOURCE_ID,

    RUDDERSTACK_WRITE_KEY: process.env.RUDDERSTACK_WRITE_KEY,
    RUDDERSTACK_DATAPLANE_URL: process.env.RUDDERSTACK_DATAPLANE_URL,

    HYPERTUNE_TOKEN: process.env.HYPERTUNE_TOKEN,
    EDGE_CONFIG: process.env.EDGE_CONFIG,
    EDGE_CONFIG_HYPERTUNE_ITEM_KEY: process.env.EDGE_CONFIG_HYPERTUNE_ITEM_KEY,
  } as v.InferInput<typeof ManagedConfigSchema>

  if (process.env.SENTRY_DSN) {
    managedConfig.SENTRY = {
      DSN: process.env.SENTRY_DSN,
      ENABLE_TUNNEL: Boolean(process.env.VERCEL_ENV),
    }
  }

  return { ...baseConfig, ...managedConfig }
}

// Create a type that represents the actual config after parsing
type ParsedConfig = v.InferOutput<typeof BaseConfigSchema> &
  v.InferOutput<typeof ManagedConfigSchema> &
  v.InferOutput<typeof EnterpriseConfigSchema>

let config: ParsedConfig

export const APP_CONFIG_WINDOW_VAR_NAME = '__APP_CONFIG__'

if (typeof document !== 'undefined') {
  // On the client we simply grab the config from window. We know it has been
  // validated on the server.
  config = window[APP_CONFIG_WINDOW_VAR_NAME]

  if (!config) throw new Error('App config was not provided in the document')
} else {
  // On the server we load the config from env vars
  const SchemaToUse = IS_ENTERPRISE_BUILD
    ? v.object({
        ...BaseConfigSchema.entries,
        ...EnterpriseConfigSchema.entries,
      })
    : v.object({
        ...BaseConfigSchema.entries,
        ...ManagedConfigSchema.entries,
      })

  const result = v.safeParse(SchemaToUse, getConfigFromEnvVars())
  if (result.success) config = result.output as ParsedConfig
  else {
    console.error(v.summarize(result.issues))
    throw new Error('Invalid config')
  }
}

export const Config = {
  ...config,

  // Attach IS_MANAGED_BUILD directly to the config without validating through
  // the schema to make sure dead code elimination works as intended.
  IS_MANAGED_BUILD,
  IS_ENTERPRISE_BUILD,
}
